import { Plus, Minus, Scan, Undo2, Redo2 } from 'lucide-react'
import clsx from 'clsx'
import { IntuiTooltip } from '@/components/intueri/IntuiTooltip'
import { getCurrentDirection } from '@/i18n/config'
import { useTranslation } from '@/i18n'
import { ZoomReadout, type ZoomReadoutProps } from '@/components/intueri/ZoomReadout'

// Re-exported so existing imports keep working; the widget itself is i18n-free
// (label is a prop) and lives in its own module so toolbar surfaces can reuse
// it without pulling in the i18n runtime.
export { ZoomReadout, type ZoomReadoutProps }

/**
 * The one zoom-control pill shared by every zoomable surface (FS-562).
 *
 * Replaces the byte-for-byte-duplicated controls that used to live in
 * IntelligentBoard, KnowledgeGraph, and GraphEditor. Pair it with `useZoom`
 * (which owns the math and the percent-space clamps) so the chrome, the
 * keyboard/type-in behavior, and the 10% increments are identical everywhere.
 *
 * Layout (each block optional except +/-):
 *   [undo][redo] | [−] {percent%} [+] | [fit]
 *
 * The `{percent%}` readout is click-to-edit when `onSetPercent` is provided:
 * click it, type a number, Enter/blur commits (the parent's `useZoom.setPercent`
 * snaps it to the 10% grid and clamps it), Escape cancels.
 */

export interface ZoomControlsUndoRedo {
  onUndo: () => void
  onRedo: () => void
  canUndo?: boolean
  canRedo?: boolean
}

export interface ZoomControlsProps {
  /** Current zoom, already in display percent (from `useZoom.percent`). */
  percent: number
  onZoomIn: () => void
  onZoomOut: () => void
  /** When provided, the percent readout becomes a click-to-edit type-in field. */
  onSetPercent?: (percent: number) => void
  /** Disable the +/- button at a boundary (defaults to enabled). */
  canZoomIn?: boolean
  canZoomOut?: boolean
  /** When provided, renders a fit-to-screen button after a divider. */
  onFit?: () => void
  /** When provided, renders undo/redo buttons before the zoom buttons. */
  undoRedo?: ZoomControlsUndoRedo
  /**
   * Self-position the pill in a corner of the nearest positioned ancestor.
   * Omit to render a plain pill the parent positions itself (Knowledge Graph).
   */
  placement?: 'bottom-left' | 'bottom-right'
  className?: string
  /**
   * `compact` is the Knowledge Graph minimap card's row (DSGN-63): a 36px pill
   * with 28px buttons and a 12px readout, so the card stays the design's
   * ~150px. Default is the 44px pill every other canvas uses.
   */
  size?: 'default' | 'compact'
  /**
   * `embedded` drops the pill chrome (border, shadow, radius) and spreads the
   * controls across the full width of whatever holds them — the Knowledge
   * Graph's minimap card draws the row under its thumbnail, behind a divider,
   * the same width as the thumbnail (DSGN-63 design). Default is the pill.
   */
  variant?: 'pill' | 'embedded'
}

const PILL_CLASS =
  'bg-white dark:bg-dark-card border border-[#e8eaee] dark:border-dark-border rounded-xl shadow-[0_2px_12px_rgba(15,23,42,0.08)] h-11 flex items-center px-1'
const BTN_CLASS =
  'flex items-center justify-center rounded-lg size-9 text-foreground hover:bg-black/[0.05] dark:hover:bg-white/10 transition-colors disabled:opacity-40 disabled:pointer-events-none'
const DIVIDER_CLASS = 'h-7 w-px bg-[#e8eaee] dark:bg-dark-border mx-0.5'

const COMPACT = {
  pill: 'bg-white dark:bg-dark-card border border-[#e8eaee] dark:border-dark-border rounded-xl shadow-[0_2px_12px_rgba(15,23,42,0.08)] h-9 flex items-center px-0.5',
  btn: 'flex items-center justify-center rounded-md size-7 text-foreground hover:bg-black/[0.05] dark:hover:bg-white/10 transition-colors disabled:opacity-40 disabled:pointer-events-none',
  divider: 'h-5 w-px bg-[#e8eaee] dark:bg-dark-border mx-0.5',
  readout: 'text-xs font-semibold text-foreground min-w-[40px] h-7 leading-7 text-center select-none tabular-nums rounded-md hover:bg-black/[0.05] dark:hover:bg-white/10 transition-colors cursor-text',
  readoutInput: 'text-xs font-semibold text-foreground min-w-[40px] h-7 text-center select-none tabular-nums bg-transparent outline-none rounded-md ring-1 ring-inset ring-[#e8eaee] dark:ring-dark-border focus:ring-2 focus:ring-blue-500',
  embedded: 'flex h-9 w-full items-center justify-between px-0.5',
}

export function ZoomControls({
  percent,
  onZoomIn,
  onZoomOut,
  onSetPercent,
  canZoomIn = true,
  canZoomOut = true,
  onFit,
  undoRedo,
  placement,
  className,
  size = 'default',
  variant = 'pill',
}: ZoomControlsProps) {
  const { t } = useTranslation()
  const isRTL = getCurrentDirection() === 'rtl'
  const compact = size === 'compact'
  const pillClass = variant === 'embedded' ? COMPACT.embedded : compact ? COMPACT.pill : PILL_CLASS
  const btnClass = compact ? COMPACT.btn : BTN_CLASS
  const dividerClass = compact ? COMPACT.divider : DIVIDER_CLASS

  const pill = (
    <div className={pillClass}>
      {undoRedo && (
        <>
          <IntuiTooltip content={t('board:toolbar.undo', { defaultValue: 'Undo' })} side="top">
            <button
              type="button"
              onClick={undoRedo.onUndo}
              disabled={undoRedo.canUndo === false}
              aria-label={t('board:toolbar.undo', { defaultValue: 'Undo' })}
              className={btnClass}
            >
              <Undo2 className="w-4 h-4" />
            </button>
          </IntuiTooltip>
          <IntuiTooltip content={t('board:toolbar.redo', { defaultValue: 'Redo' })} side="top">
            <button
              type="button"
              onClick={undoRedo.onRedo}
              disabled={undoRedo.canRedo === false}
              aria-label={t('board:toolbar.redo', { defaultValue: 'Redo' })}
              className={btnClass}
            >
              <Redo2 className="w-4 h-4" />
            </button>
          </IntuiTooltip>
          <div className={dividerClass} />
        </>
      )}

      <IntuiTooltip content={t('board:toolbar.zoom_out', { defaultValue: 'Zoom out' })} side="top">
        <button
          type="button"
          onClick={onZoomOut}
          disabled={!canZoomOut}
          aria-label={t('board:toolbar.zoom_out', { defaultValue: 'Zoom out' })}
          className={btnClass}
        >
          <Minus className="w-4 h-4" />
        </button>
      </IntuiTooltip>

      <ZoomReadout
        percent={percent}
        onSetPercent={onSetPercent}
        label={t('common:zoom.level', { defaultValue: 'Zoom level' })}
        className={compact ? COMPACT.readout : undefined}
        inputClassName={compact ? COMPACT.readoutInput : undefined}
      />

      <IntuiTooltip content={t('board:toolbar.zoom_in', { defaultValue: 'Zoom in' })} side="top">
        <button
          type="button"
          onClick={onZoomIn}
          disabled={!canZoomIn}
          aria-label={t('board:toolbar.zoom_in', { defaultValue: 'Zoom in' })}
          className={btnClass}
        >
          <Plus className="w-4 h-4" />
        </button>
      </IntuiTooltip>

      {onFit && (
        <>
          <div className={dividerClass} />
          <IntuiTooltip
            content={t('board:toolbar.fit_to_screen', { defaultValue: 'Fit to screen' })}
            side="top"
          >
            <button
              type="button"
              onClick={onFit}
              aria-label={t('board:toolbar.fit_to_screen', { defaultValue: 'Fit to screen' })}
              className={btnClass}
            >
              <Scan className="w-4 h-4" />
            </button>
          </IntuiTooltip>
        </>
      )}
    </div>
  )

  if (!placement) {
    return className ? <div className={className}>{pill}</div> : pill
  }

  // Self-positioned: bottom corner, flipped for RTL so it never collides with
  // the (mirrored) chat / panels.
  const corner =
    placement === 'bottom-right'
      ? isRTL
        ? 'left-4'
        : 'right-4'
      : isRTL
        ? 'right-4'
        : 'left-4'

  return (
    <div className={clsx('absolute bottom-4 z-20 flex items-center', corner, className)}>{pill}</div>
  )
}

export default ZoomControls
